export const TOUR_PAD = 10;
export const TOUR_TIP_WIDTH = 360;
export const TOUR_VIEWPORT_PAD = 4;
export const TOUR_SPOTLIGHT_RADIUS = 16;

export interface TourTargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function readTourTargetRect(controlId: string | null): TourTargetRect | null {
  if (!controlId) return null;
  const candidates = [controlId, `${controlId}.mobile`];
  for (const candidate of candidates) {
    const node = document.querySelector(`[data-control-id="${candidate}"]`);
    if (!(node instanceof HTMLElement)) continue;
    const rect = node.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    // Prefer the visible target (desktop rail is hidden below md).
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") continue;
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
  }
  return null;
}

export function clampTourSpotlight(rect: TourTargetRect): TourTargetRect {
  const pad = TOUR_VIEWPORT_PAD;
  const maxWidth = Math.max(48, window.innerWidth - pad * 2);
  const maxHeight = Math.max(48, window.innerHeight - pad * 2);
  const width = Math.min(rect.width, maxWidth);
  const height = Math.min(rect.height, maxHeight);
  const left = Math.min(Math.max(pad, rect.left), window.innerWidth - pad - width);
  const top = Math.min(Math.max(pad, rect.top), window.innerHeight - pad - height);
  return { top, left, width, height };
}

/** Keep rail/mobile-nav spotlights inside their blue/nav surface (no spill into the page). */
export function clampTourSpotlightToTargetSurface(
  rect: TourTargetRect,
  controlId: string | null,
): TourTargetRect {
  const clamped = clampTourSpotlight(rect);
  if (!controlId?.startsWith("nav.")) return clamped;

  const candidates = [controlId, `${controlId}.mobile`];
  let surface: HTMLElement | null = null;
  for (const candidate of candidates) {
    const node = document.querySelector(`[data-control-id="${candidate}"]`);
    if (!(node instanceof HTMLElement)) continue;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") continue;
    const aside = node.closest("aside");
    if (aside instanceof HTMLElement) {
      surface = aside;
      break;
    }
    const mobileNav = node.closest("nav[aria-label]");
    if (mobileNav instanceof HTMLElement && candidate.endsWith(".mobile")) {
      surface = mobileNav;
      break;
    }
  }
  if (!surface) return clamped;

  const bounds = surface.getBoundingClientRect();
  const inset = 4;
  const left = Math.max(clamped.left, bounds.left + inset);
  const top = Math.max(clamped.top, bounds.top + inset);
  const right = Math.min(clamped.left + clamped.width, bounds.right - inset);
  const bottom = Math.min(clamped.top + clamped.height, bounds.bottom - inset);
  return {
    top,
    left,
    width: Math.max(24, right - left),
    height: Math.max(24, bottom - top),
  };
}

function clampTipPosition(
  left: number,
  top: number,
  tipHeight: number,
  tipWidth: number,
): { top: number; left: number } {
  const maxLeft = window.innerWidth - TOUR_VIEWPORT_PAD - tipWidth;
  const maxTop = window.innerHeight - TOUR_VIEWPORT_PAD - tipHeight;
  return {
    left: Math.min(Math.max(TOUR_VIEWPORT_PAD, left), Math.max(TOUR_VIEWPORT_PAD, maxLeft)),
    top: Math.min(Math.max(TOUR_VIEWPORT_PAD, top), Math.max(TOUR_VIEWPORT_PAD, maxTop)),
  };
}

export function tourTipPosition(
  target: TourTargetRect | null,
  tipHeight: number,
  tipWidth: number = TOUR_TIP_WIDTH,
): { top: number; left: number } {
  if (!target) {
    return clampTipPosition(
      window.innerWidth / 2 - tipWidth / 2,
      window.innerHeight / 2 - tipHeight / 2,
      tipHeight,
      tipWidth,
    );
  }

  const gap = 16;
  const pad = TOUR_VIEWPORT_PAD;
  const placements = [
    { left: target.left + target.width + gap, top: target.top },
    { left: target.left - tipWidth - gap, top: target.top },
    {
      left: target.left + target.width / 2 - tipWidth / 2,
      top: target.top + target.height + gap,
    },
    {
      left: target.left + target.width / 2 - tipWidth / 2,
      top: target.top - tipHeight - gap,
    },
  ];

  for (const placement of placements) {
    const fitsHorizontally = placement.left >= pad && placement.left + tipWidth <= window.innerWidth - pad;
    const fitsVertically = placement.top >= pad && placement.top + tipHeight <= window.innerHeight - pad;
    if (fitsHorizontally && fitsVertically) return placement;
  }

  return clampTipPosition(placements[0]!.left, placements[0]!.top, tipHeight, tipWidth);
}
